import { useMemo } from 'react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  useIsMobile,
} from '@hotelise/ui';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { differenceInDays, format, addMonths, startOfMonth, isBefore, isEqual } from 'date-fns';
import type { MergedGuest } from './guestUtils';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface Props {
  guests: MergedGuest[];
  /** Selected date range — used to fill empty months on x-axis */
  dateRange?: DateRange;
}

export default function GuestLeadTimeChart({ guests, dateRange }: Props) {
  const isMobile = useIsMobile();

  const { chartData, totalWithData, totalReservations } = useMemo(() => {
    // Collect lead-time data grouped by year-month
    const monthData = new Map<string, { sum: number; count: number }>();
    let resCount = 0;
    let withBookedAt = 0;

    guests.forEach((g) => {
      g.reservations.forEach((r) => {
        resCount += 1;
        if (!r.bookedAt) return;
        const leadDays = differenceInDays(r.checkin, r.bookedAt);
        if (leadDays < 0) return;
        withBookedAt += 1;
        const key = format(r.checkin, 'yyyy-MM');
        const entry = monthData.get(key) || { sum: 0, count: 0 };
        entry.sum += leadDays;
        entry.count += 1;
        monthData.set(key, entry);
      });
    });

    // Build full list of months from date range (fill gaps with zero)
    const allMonthKeys: string[] = [];
    if (dateRange?.from && dateRange?.to) {
      let cursor = startOfMonth(dateRange.from);
      const end = startOfMonth(dateRange.to);
      while (isBefore(cursor, end) || isEqual(cursor, end)) {
        allMonthKeys.push(format(cursor, 'yyyy-MM'));
        cursor = addMonths(cursor, 1);
      }
    } else {
      allMonthKeys.push(...Array.from(monthData.keys()).sort());
    }

    const yearSet = new Set(allMonthKeys.map((key) => key.slice(0, 4)));
    const monthLabelFormat = yearSet.size > 1 ? 'MMM yy' : 'MMM';
    const data = allMonthKeys.map((key) => {
      const entry = monthData.get(key);
      return {
        month: format(new Date(`${key}-15`), monthLabelFormat),
        avgLeadTime: entry ? Math.round(entry.sum / entry.count) : 0,
        bookings: entry?.count ?? 0,
      };
    });

    return { chartData: data, totalWithData: withBookedAt, totalReservations: resCount };
  }, [guests, dateRange]);

  const chartConfig = {
    avgLeadTime: { label: 'Avg Lead Time (days)', color: 'hsl(210, 80%, 55%)' },
  };

  const overallAvg = useMemo(() => {
    const withData = chartData.filter((d) => d.bookings > 0);
    if (withData.length === 0) return 0;
    const totalDays = withData.reduce((s, d) => s + d.avgLeadTime * d.bookings, 0);
    const totalBookings = withData.reduce((s, d) => s + d.bookings, 0);
    return Math.round(totalDays / totalBookings);
  }, [chartData]);

  const hasData = totalWithData > 0;
  const coverage = totalReservations > 0
    ? Math.round((totalWithData / totalReservations) * 100)
    : 0;

  return (
    <div className="bg-card rounded-lg border p-4 md:p-6 overflow-hidden min-w-0">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div>
          <h2 className="text-sm font-semibold">Avg Lead Time</h2>
          {hasData ? (
            <p className="text-xs text-muted-foreground">
              ~{overallAvg} days avg
              {coverage < 100 && (
                <span className="ml-1 text-muted-foreground/60">
                  ({coverage}% of bookings have date data)
                </span>
              )}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">No booking date data available</p>
          )}
        </div>
      </div>

      {hasData ? (
        <ChartContainer config={chartConfig} className={`${isMobile ? 'h-[200px]' : 'h-[260px]'} w-full [&>div]:!aspect-auto`}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: isMobile ? 10 : 12 }} interval={0} />
            <YAxis allowDecimals={false} tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 25 : 35} />
            <ChartTooltip content={<ChartTooltipContent hideLabel={false} />} />
            <Line
              type="monotone"
              dataKey="avgLeadTime"
              stroke="hsl(210, 80%, 55%)"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ChartContainer>
      ) : (
        <div className={`flex items-center justify-center ${isMobile ? 'h-[200px]' : 'h-[260px]'} text-muted-foreground text-sm`}>
          Booking creation dates are not available for the selected range.
        </div>
      )}
    </div>
  );
}
